import React, { useReducer } from 'react';
import ErrorText from '@/components/ErrorText';
import Label from '@/components/Label';
import Button, { ButtonSizes, ButtonVariants } from '@/components/new-design/Button';
import { yupResolver } from '@hookform/resolvers/yup';
import { BaseSyntheticEvent, FC } from 'react';
import { useForm } from 'react-hook-form';
import { isAddress } from 'viem';
import * as yup from 'yup';
import { withdrawUsdt } from '../web3/withdrawUsdt';
import { useAccountAbstractionStore } from '@/features/account-abstraction/store/AccountAbstractionContext';
import config from '@/common/config';
import { LiaExternalLinkAltSolid } from 'react-icons/lia';
import friendsSVG from '../../../assets/friends.svg';
import Select from '@/components/Select';
import { Referral } from '@/services/referrals/referralsServicesTypes';
import { Friend } from '@/services/friends/friendsServicesTypes';
import { usePostTransfer } from '@/services/wallets/queries/usePostTransfer';
import { useSelector } from 'react-redux';
import { ReducerState } from '@/services/common/lib/reducerState';
import TransferFundsBox from '@/features/user-profile/components/TransferFundsBox';

interface State {
  loading: boolean;
  error: boolean;
  errorMessage: string | null;
  success: boolean;
  txHash: string | null;
}

type Action =
  | { type: 'START' }
  | { type: 'SUCCESS'; payload: string }
  | { type: 'ERROR'; payload: string }
  | { type: 'RESET' };

const initialState: State = {
  loading: false,
  error: false,
  errorMessage: null,
  success: false,
  txHash: null,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'START':
      return { ...state, loading: true, error: false, errorMessage: null };
    case 'SUCCESS':
      return {
        ...state,
        loading: false,
        success: true,
        txHash: action.payload,
      };
    case 'ERROR':
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: action.payload,
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

interface SendFriendsModalProps {
  balance: string;
  onClose: () => void;
  users: (Referral | Friend)[];
  inputPlaceHolder: string;
  defaultTransferId?: string;
  friends: Friend[];
}

interface FormInputs {
  amount: number;
  walletAddress: {
    smartWalletAddress: string;
  };
}

export const validationSchema = (balance: number) =>
  yup
    .object({
      amount: yup
        .number()
        .required('Amount is required')
        .test('balance', 'Insufficient balance', (value) => !!value && value <= balance)
        .transform((value) => {
          if (isNaN(value)) {
            return 0;
          }
          return value;
        }),
      walletAddress: yup
        .object({
          smartWalletAddress: yup
            .string()
            .test('address', 'Enter valid address', (value) => !!value && isAddress(value))
            .required('Wallet Address is required'),
        })
        .required('Wallet Address is required'),
    })
    .required();

export const SendFriendsTab: FC<SendFriendsModalProps> = ({
  balance,
  onClose,
  users,
  inputPlaceHolder,
  defaultTransferId,
  friends = [],
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const accountAbstractionStore = useAccountAbstractionStore();
  const { mutate: postTransfer } = usePostTransfer();

  const user = useSelector((state: ReducerState) => state.auth);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
  } = useForm<FormInputs>({
    resolver: yupResolver(validationSchema(Number(balance))),
    mode: 'onChange',
    defaultValues: {
      walletAddress: users.find((user) => user.id === defaultTransferId)?.smartWalletAddress
        ? {
            smartWalletAddress: users.find((user) => user.id === defaultTransferId)?.smartWalletAddress,
          }
        : users.length > 0
          ? { smartWalletAddress: users[0].smartWalletAddress }
          : { smartWalletAddress: '' },
    },
  });

  const onSubmit = async (data: FormInputs, e?: BaseSyntheticEvent<object, any, any>) => {
    e?.preventDefault();
    dispatch({ type: 'START' });
    try {
      const tx = await withdrawUsdt(
        accountAbstractionStore.state,
        data.walletAddress?.smartWalletAddress as `0x${string}`,
        data.amount,
      );

      if (tx?.success) {
        dispatch({ type: 'SUCCESS', payload: tx.receipt.transactionHash });
        const selectedUser = users.find((user) => user?.smartWalletAddress === data?.walletAddress.smartWalletAddress);
        const isFriend = friends.some((friend: Friend) => friend.id === selectedUser?.id);

        postTransfer({
          isSelfTransfer: false,
          txId: tx.receipt.transactionHash,
          from: user.alias,
          to: selectedUser?.alias,
          friendId: isFriend ? selectedUser?.id : undefined,
          referralId: !isFriend ? selectedUser?.id : undefined,
          amount: data.amount,
        });
      }
    } catch (e) {
      if (e instanceof Error) {
        dispatch({ type: 'ERROR', payload: e.message });
      } else dispatch({ type: 'ERROR', payload: '' });
    }
  };

  // const handleCopyToClipboard = () => {
  //   toast({ description: 'Copied to clipboard', variant: 'success' });
  //   return navigator.clipboard.writeText(state.txHash || '');
  // };

  const handleRetry = () => {
    dispatch({ type: 'RESET' });
  };

  return (
    <div className="left-4 flex min-w-[min(650px,calc(100vw-50px))] max-w-[min(650px,calc(100vw-50px))] items-center justify-center">
      <div className="flex h-full w-full flex-col gap-5 rounded-[30px] bg-ph-background-dark">
        {!state.success && !state.error && (
          <form onSubmit={handleSubmit(onSubmit)} className="flex w-full flex-grow flex-col gap-2">
            <div className="flex flex-col gap-1 pb-4">
              <Label semiBold>Transfer to</Label>
              <div className="relative">
                <img src={friendsSVG} alt="friends" className="absolute left-6 top-2.5 z-30" width={24} />
                <Select
                  options={users}
                  labelKey="alias"
                  onChange={(value) => {
                    setValue(
                      'walletAddress',
                      { smartWalletAddress: value?.smartWalletAddress },
                      { shouldValidate: true },
                    );
                  }}
                  placeholderClassName="pl-16"
                  placeholder={inputPlaceHolder}
                  hideInput={true}
                  defaultSelectedLabelKey={
                    defaultTransferId ? users.find((user) => user.id === defaultTransferId)?.alias : ''
                  }
                />
              </div>
              {errors.walletAddress?.smartWalletAddress && (
                <ErrorText>* {errors.walletAddress.smartWalletAddress.message}</ErrorText>
              )}
            </div>
            <TransferFundsBox
              isValid={isValid}
              state={state}
              onClose={onClose}
              loadingLabel="Processing"
              balance={Number(balance)}
              register={register}
              errors={errors}
            />
          </form>
        )}
        {state.error && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold text-ph-red">Transaction Failed:</p>
              <span className="flex items-center gap-1">
                {state.errorMessage ? (
                  <p title={state.errorMessage || ''} className="overflow-hidden overflow-ellipsis text-sm text-ph-red">
                    {state.errorMessage}
                  </p>
                ) : (
                  <p className="overflow-hidden overflow-ellipsis text-sm text-ph-red">
                    There was an error with your transaction. If this keeps happening please contact{' '}
                    <a type="email" className="text-ph-blue" href="mailto:support@phenompoker.com">
                      support@phenompoker.com
                    </a>
                  </p>
                )}
              </span>
            </div>
            <div className="flex gap-1">
              <Button onClick={handleRetry} variant={ButtonVariants.primary} size={ButtonSizes.lg}>
                Try Again
              </Button>
              <Button onClick={onClose} variant={ButtonVariants.secondary} size={ButtonSizes.lg}>
                Close
              </Button>
            </div>
          </div>
        )}
        {state.success && (
          <div className="flex flex-col gap-5">
            <h3 className="text-lg text-ph-gray-white">Transaction Submitted!</h3>
            <a
              title={`${config.blockExplorerUrl}/tx/${state.txHash}`}
              href={`${config.blockExplorerUrl}/tx/${state.txHash}`}
              target="_blank"
              rel="noreferrer"
              className="text-ph-blue"
            >
              <span className="flex items-center gap-1">
                <p>View on Block Explorer </p>
                <LiaExternalLinkAltSolid size={20} className="text-ph-blue" />
              </span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
